import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import colors from "../../../assets/theme/colors";
import { getPhoneHref } from "../../../utils/contactPhone";
import CareProfileStoryShareSheet from "./CareProfileStoryShareSheet";

const storyTheme = colors.semantic.profileFlow.careStory;

const getTone = (toneKey = "who") => storyTheme.tones[toneKey] || storyTheme.tones.who;

const getInitial = (name = "") => String(name || "C").trim().charAt(0).toUpperCase() || "C";

const StoryIdentityCard = ({ story, readMode = false }) => {
  const profile = story.profile || {};
  const emergency = (story.chapters || []).find((chapter) => chapter.id === "emergency");
  const primaryContact = emergency?.contacts?.[0]?.name || "";
  const profileLine = [
    profile.age ? `Age ${profile.age}` : "",
    profile.careFocus,
  ].filter(Boolean).join(" · ");

  return (
    <Box
      sx={{
        p: { xs: 1.75, sm: readMode ? 2.8 : 2.2 },
        borderRadius: readMode ? "28px" : "24px",
        border: `1px solid ${storyTheme.cardBorder}`,
        bgcolor: storyTheme.cardBg,
        boxShadow: `0 10px 24px ${storyTheme.cardShadow}`,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "auto minmax(0, 1fr) auto" },
        alignItems: { xs: "start", sm: "center" },
        gap: { xs: 1.25, sm: 2 },
      }}
    >
      <Box
        sx={{
          width: { xs: 70, sm: readMode ? 96 : 78 },
          height: { xs: 70, sm: readMode ? 96 : 78 },
          borderRadius: "50%",
          bgcolor: storyTheme.tones.who.markerBg,
          color: storyTheme.tones.who.headerInk,
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          flexShrink: 0,
          fontSize: { xs: "1.5rem", sm: readMode ? "2rem" : "1.8rem" },
          fontWeight: 900,
          mx: { xs: "auto", sm: 0 },
        }}
      >
        {profile.photoURL ? (
          <Box
            component="img"
            src={profile.photoURL}
            alt={profile.displayName || "Profile photo"}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : getInitial(profile.displayName)}
      </Box>
      <Box sx={{ minWidth: 0, textAlign: { xs: "center", sm: "left" } }}>
        <Typography sx={{ color: storyTheme.text, fontSize: { xs: "1.5rem", sm: readMode ? "2rem" : "1.75rem" }, fontWeight: 950, lineHeight: 1.08 }}>
          {profile.displayName || "Care profile"}
        </Typography>
        <Typography sx={{ mt: 0.35, color: storyTheme.muted, fontSize: { xs: "0.92rem", sm: readMode ? "1.04rem" : "1rem" }, lineHeight: 1.25 }}>
          {profileLine || "Care story profile"}
        </Typography>
      </Box>
      <Box
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          alignItems: "center",
          gap: 0.65,
          px: 1.35,
          py: 0.65,
          borderRadius: 999,
          bgcolor: storyTheme.primarySoft,
          color: storyTheme.primaryDark,
          fontWeight: 850,
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: 17 }} />
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 850 }}>
          {primaryContact || "Story ready"}
        </Typography>
      </Box>
    </Box>
  );
};

const StoryCard = ({ chapter, onEditChapter, canEditProfile = true, readMode = false }) => {
  const tone = getTone(chapter.tone);

  return (
    <Box
      sx={{
        borderRadius: readMode ? "26px" : "24px",
        border: `1px solid ${tone.border}`,
        bgcolor: storyTheme.cardBg,
        boxShadow: `0 10px 24px ${storyTheme.cardShadow}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          minHeight: readMode ? 54 : 58,
          px: { xs: 1.6, sm: 2.15 },
          py: readMode ? 1.1 : 1.25,
          bgcolor: tone.headerBg,
          borderBottom: `1px solid ${tone.border}`,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: tone.headerInk,
            fontSize: { xs: "0.72rem", sm: "0.8rem" },
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            lineHeight: 1.25,
          }}
        >
          {chapter.title}
        </Typography>
        {canEditProfile && !readMode ? (
          <Button
            variant="text"
            onClick={() => onEditChapter?.(chapter)}
            startIcon={<SettingsOutlinedIcon sx={{ fontSize: 17 }} />}
            sx={{
              minWidth: 0,
              px: { xs: 0.95, sm: 1.2 },
              py: 0.55,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 900,
              color: tone.chipInk,
              bgcolor: tone.chipBg,
              "&:hover": { bgcolor: alpha(tone.chipBg, 0.72) },
            }}
          >
            Edit
          </Button>
        ) : null}
      </Box>
      <Stack spacing={1.5} sx={{ px: { xs: 1.6, sm: 2.35 }, py: { xs: 1.8, sm: 2.25 } }}>
        {chapter.paragraphs.length ? (
          <Stack spacing={1.25}>
            {chapter.paragraphs.map((paragraph, index) => (
              <Typography
                key={`${chapter.id}-paragraph-${index}`}
                sx={{
                  color: storyTheme.text,
                  fontSize: { xs: "0.94rem", sm: readMode ? "1rem" : "1.12rem" },
                  lineHeight: 1.62,
                  fontWeight: 450,
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Stack>
        ) : null}

        {chapter.highlights.length ? (
          <Stack spacing={0.8}>
            {chapter.highlights.map((item) => (
              <Box
                key={`${chapter.id}-${item.label}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "150px minmax(0, 1fr)" },
                  gap: { xs: 0.15, sm: 1 },
                  alignItems: "baseline",
                }}
              >
                <Typography sx={{ color: tone.headerInk, fontSize: "0.76rem", fontWeight: 900 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: storyTheme.text, fontSize: "0.88rem", lineHeight: 1.45 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : null}

        {chapter.callout ? (
          <Box
            sx={{
              p: { xs: 1.2, sm: 1.45 },
              borderRadius: "16px",
              border: `1px solid ${storyTheme.warningBorder}`,
              bgcolor: storyTheme.warningBg,
              color: storyTheme.warningText,
              display: "grid",
              gridTemplateColumns: "auto minmax(0, 1fr)",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 900 }}>!</Typography>
            <Typography sx={{ fontSize: { xs: "0.9rem", sm: "1rem" }, lineHeight: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 850 }}>{chapter.callout.label}: </Box>
              {chapter.callout.text}
            </Typography>
          </Box>
        ) : null}

        {chapter.contacts.length ? (
          <Stack spacing={0}>
            {chapter.contacts.map((contact, index) => {
              const phoneHref = getPhoneHref(contact.detail);
              return (
                <Box
                  key={`${contact.name}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 1.15,
                    py: 1.1,
                    borderTop: index === 0 ? "none" : `1px solid ${storyTheme.cardBorder}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "50%",
                      bgcolor: tone.markerBg,
                      color: tone.headerInk,
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 900,
                    }}
                  >
                    {getInitial(contact.name)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: storyTheme.text, fontSize: "0.95rem", fontWeight: 900 }}>
                      {contact.name}
                    </Typography>
                    {contact.detail ? (
                      <Typography sx={{ mt: 0.15, color: storyTheme.muted, fontSize: "0.88rem" }}>
                        {contact.detail}
                      </Typography>
                    ) : null}
                  </Box>
                  {phoneHref ? (
                    <Button
                      component="a"
                      href={phoneHref}
                      startIcon={<CallOutlinedIcon sx={{ fontSize: 17 }} />}
                      sx={{
                        borderRadius: 999,
                        px: 1.2,
                        textTransform: "none",
                        fontWeight: 900,
                        bgcolor: storyTheme.primarySoft,
                        color: storyTheme.primaryDark,
                        "&:hover": { bgcolor: alpha(storyTheme.primarySoft, 0.72) },
                      }}
                    >
                      Call
                    </Button>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        ) : null}

        {!chapter.hasContent ? (
          <Typography sx={{ color: storyTheme.muted, fontSize: { xs: "0.92rem", sm: "1rem" }, lineHeight: 1.5 }}>
            {chapter.emptyText}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

const CareProfileStoryReview = ({
  story,
  onEditChapter,
  canEditProfile = true,
  mode = "review",
  showEmptySections = true,
}) => {
  const [shareOpen, setShareOpen] = useState(false);
  const isReadMode = mode === "read";
  const editable = canEditProfile && !isReadMode;
  const visibleChapters = showEmptySections
    ? (story?.chapters || [])
    : (story?.chapters || []).filter((chapter) => chapter.hasContent);
  const shareLabel = story?.profile?.displayName
    ? `Share ${story.profile.displayName}'s Story`
    : "Share Story";

  if (!story) return null;

  return (
    <Box
      sx={{
        bgcolor: storyTheme.pageBg,
        px: { xs: 1.15, sm: isReadMode ? 1.75 : 1.5 },
        py: { xs: 1.15, sm: isReadMode ? 1.75 : 1.5 },
        borderRadius: { xs: 0, sm: isReadMode ? 4 : 3 },
      }}
    >
      <Stack spacing={isReadMode ? 1.5 : 2.15} sx={{ maxWidth: 900, mx: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
            alignItems: "start",
            gap: 1.2,
            pb: isReadMode ? 1.15 : 0.5,
            borderBottom: `1px solid ${storyTheme.cardBorder}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component={isReadMode ? "h1" : "h2"}
              sx={{
                m: 0,
                color: storyTheme.text,
                fontSize: { xs: "1.35rem", sm: isReadMode ? "1.55rem" : "1.7rem" },
                fontWeight: 900,
                lineHeight: 1.15,
              }}
            >
              {story.title}
            </Typography>
            {!isReadMode ? (
              <Typography sx={{ mt: 0.45, color: colors.semantic.ink, fontSize: { xs: "0.92rem", sm: "1rem" }, lineHeight: 1.45 }}>
                {story.subtitle}
              </Typography>
            ) : null}
          </Box>
          <Button
            variant="outlined"
            startIcon={<IosShareRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setShareOpen(true)}
            sx={{
              alignSelf: { xs: "flex-start", sm: "auto" },
              borderRadius: 999,
              px: { xs: 1.25, sm: 1.55 },
              py: 0.85,
              textTransform: "none",
              fontSize: { xs: "0.82rem", sm: "0.88rem" },
              fontWeight: 900,
              borderColor: storyTheme.primary,
              color: storyTheme.primaryDark,
              bgcolor: storyTheme.cardBg,
              "&:hover": {
                borderColor: storyTheme.primaryDark,
                bgcolor: storyTheme.primarySoft,
              },
            }}
          >
            {shareLabel}
          </Button>
        </Box>

        {!isReadMode ? <StoryIdentityCard story={story} /> : null}

        <Stack spacing={isReadMode ? 1.6 : 2}>
          {visibleChapters.map((chapter) => (
            <StoryCard
              key={chapter.id}
              chapter={chapter}
              canEditProfile={editable}
              readMode={isReadMode}
              onEditChapter={onEditChapter}
            />
          ))}
        </Stack>
      </Stack>
      <CareProfileStoryShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        story={story}
      />
    </Box>
  );
};

export default CareProfileStoryReview;
